<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import StatusBadge from '@/components/utilities/StatusBadge.vue';
import { getImageUrlWithCustomWidth } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';
import { BuildingStatusEnum, getBuildingStatusString } from '@/enums/BuildingStatus';
import { useDate } from '@/composables/useDate.ts';


const { getClosestAvailabilityDate } = useDate();

const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const props = defineProps<{
  building: Building;
  href: string;
  mode?: 'rental' | 'purchase';
}>();

const emit = defineEmits<{
  click: [building: Building];
}>();

const isAvailable = computed(() => {
  return props.building.availableUnits !== null &&
         props.building.availableUnits !== undefined &&
         props.building.availableUnits > 0;
});

function handleClick(event: Event) {
  // Empêcher le clic si pas d'unités disponibles
  if (!isAvailable.value) {
    event.preventDefault();
    return;
  }
  
  if (!props.href) {
    event.preventDefault();
    emit('click', props.building);
  }
}

const imageIsLoading = ref(true);

const buildingImage = computed(() => {
  if (props.building.buildingImageUrl) {
    return getImageUrlWithCustomWidth(props.building.buildingImageUrl, 500);
  }
  return undefined;
});

const buildingAddress = computed(() => {
  const parts = [];
  if (props.building.no) {
    parts.push(props.building.no);
  }
  if (props.building.localizedStreet) {
    parts.push(props.building.localizedStreet);
  }
  return parts.join(' ');
});

const availableUnitsText = computed(() => {
  if (props.building.availableUnits === null || props.building.availableUnits === undefined || props.building.availableUnits === 0) {
    return t('browsingApp.buildings.noAvailableUnits');
  }
  return t('browsingApp.buildings.availableUnitsWithCount', { count: props.building.availableUnits }, props.building.availableUnits);
});

function buildingHasPromotion(building: Building): boolean {
  return Boolean(building.hasPromotion);
}
</script>

<template>
  <div class="cardTopImage-container">
    <component :is="href && isAvailable ? 'a' : 'div'"
               :href="(href && isAvailable) ? href : undefined"
               :class="{ 'buildingCard--disabled': !isAvailable }"
               @click="handleClick">
      <div class="cardTopImage cardTopImage_buildingLinkCard"
           :data-building-status="building.status">
        <div class="cardTopImage-headerContainer">
          <h3 class="cardTopImage-heading">
            {{ buildingAddress }}
          </h3>
          
          <div class="cardTopImage-statusContainer">
            <StatusBadge v-if="isAvailable"
                          class="buildingLinkCard-statusBadge"
                         :status="getBuildingStatusString(building.status)"
                         :label="t(`browsingApp.statuses.buildingStatus.${getBuildingStatusString(building.status)}`)" />
            <div v-if="isAvailable && buildingHasPromotion(building)" class="tagPromotion">{{ t('unitSummary.promotion') }}</div>
          </div>
        </div>
        <div class="cardTopImage-imageContainer">
          <div v-if="buildingImage"
              class="cardTopImage-carousel">
            <img :src="buildingImage"
                alt=""
                class="cardTopImage-image"
                :data-image-is-loading="imageIsLoading"
                @load="imageIsLoading = false" />
          </div>
          <div v-else
              class="cardTopImage-noImage">
            {{ t('common.noImageAvailable') }}
          </div>
        </div>
        <div class="cardTopImage-contentContainer">
          <dl class="buildingLinkCard-data">
            <div class="buildingLinkCard-dataGroup buildingLinkCard-project">
              <dt>{{ t('browsingApp.buildings.project') }}</dt>
              <dd>{{ building.localizedProjectName }}</dd>
            </div>
            <div class="buildingLinkCard-dataGroup buildingLinkCard-region">
              <dt>{{ t('browsingApp.buildings.region') }}</dt>
              <dd>{{ building.localizedRegionName }}</dd>
            </div>
            <div v-if="building.postalCode" class="buildingLinkCard-dataGroup buildingLinkCard-postalCode">
              <dt>{{ t('browsingApp.buildings.postalCode') }}</dt>
              <dd>{{ building.postalCode }}</dd>
            </div>
            <div class="buildingLinkCard-dataGroup buildingLinkCard-availableUnits">
              <dt>{{ t('browsingApp.buildings.availableUnits') }}</dt>
              <dd>{{ availableUnitsText }}</dd>
            </div>
            <div class="buildingLinkCard-dataGroup buildingLinkCard-closestAvailabilityDate">
              <dt>{{ props.mode === 'purchase' ? t('browsingApp.buildings.closestAvailabilityDate') : t('unitSummary.moveInDate') }}</dt>
              <dd v-if="building.status !== BuildingStatusEnum.Full">{{ getClosestAvailabilityDate(building.closestAvailabilityDate) }}</dd>
              <dd v-else>{{ t('common.notAvailableAbbreviated') }}</dd>
            </div>
          </dl>
        </div>
        <div v-if="$slots.footer"
            class="buildingLinkCard-footer">
          <slot name="footer">
          </slot>
        </div>
        <span v-if="href && isAvailable" class="buildingLinkCard-viewAction">
          {{ t('browsingApp.buildings.viewBuilding') }}
          <span class="buildingLinkCard-arrow">
            <svg width="17"
                 height="14"
                 viewBox="0 0 17 14"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M9.60179 0.0012207L8.32797 1.25257L12.9042 5.74763H0.832031V7.51625H12.9042L8.32797 12.0122L9.60179 13.2631L16.3518 6.63216L9.60179 0.0012207Z"
                    fill="currentColor" />
            </svg>
          </span>
        </span>
      </div>
    </component>
  </div>
</template>

<style lang="scss" scoped>

.buildingLinkList-buildingCard{
  a{
    text-decoration: none;
    color:var(--gray-950);

    &:hover{
      .cardTopImage_buildingLinkCard{
        outline-color: var(--primary-500);
        outline-offset: -1px;
      }
      .buildingLinkCard-arrow {
        transform: translateX(2px);
      }
    }
  }
}
.cardTopImage-container{
  > div{
    width: 100%;
    height: 100%;
  }
}
.cardTopImage_buildingLinkCard {  
  border: 2px solid var(--gray-200);
  outline: 4px solid transparent;
  outline-offset: -4px;
  border-radius: .8rem;
  background-color:#ffffff;
  padding: 3.2rem;

  .cardTopImage-headerContainer {
    display: flex;
    align-items: baseline;
    gap: 1.6rem;
    margin-bottom: 1.6rem;
    justify-content: space-between;
    flex-wrap: nowrap;
  }
  .cardTopImage-statusContainer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 0.8rem;
    flex-wrap: wrap;
  }

  .cardTopImage-heading {
    font-size: 2rem;
    font-weight: 600;
    font-family: "Inter", "Roboto", sans-serif;
    margin: 0;
    text-decoration: none;
    max-width: 50%;
  }

  .cardTopImage-titleGroup {
    margin: 0;
  }

  .cardTopImage-contentContainer {
    padding: 0;
    display: block;
  }

  .cardTopImage-locationGroup {
    margin: 2.2rem 0 4.2rem;
  }

  .cardTopImage-regionName {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 0.125rem;
  }

  .cardTopImage-subheading {
    font-size: 1.6rem;
    font-weight: 500;
  }

  .cardTopImage-image {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
  }
  .cardTopImage-noImage {
    width: 100%;
    height:100%;
    display:flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  // Status colors - adapting for buildings
  &[data-building-status="Active"] {
    --baseStatusColor: var(--green-500);
    --statusBgColor: var(--green-200);
  }

  &[data-building-status="Inactive"] {
    --baseStatusColor: var(--red-500);
    --statusBgColor: var(--red-200);
  }

  &[data-building-status="UnderConstruction"] {
    --baseStatusColor: var(--orange-500);
    --statusBgColor: var(--orange-200);
  }

  &[data-building-status="Planned"] {
    --baseStatusColor: var(--yellow-500);
    --statusBgColor: var(--yellow-200);
  }

  .buildingLinkCard-data {
    margin: 1.6rem 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;

    .buildingLinkCard-dataGroup {
      dt {
        font-size: 1.4rem;
      }

      dd {
        font-size: 1.6rem;
        font-weight: 700;
        margin: 0;
      }
    }
  }

  .buildingLinkCard-viewAction {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--primary-600);
    text-decoration: none;
  }

  .buildingLinkCard-arrow {
    font-size: 1.25rem;
    transition: transform 0.2s;
  }
}

// Styles pour les cartes désactivées
.buildingCard--disabled {
  pointer-events: none;
  
  .cardTopImage_buildingLinkCard {
    opacity: 0.6;
    filter: grayscale(0.3);
    cursor: not-allowed;
    
    &:hover {
      outline-color: transparent !important;
    }
  }
}

@media screen and (max-width: 384px){
  .cardTopImage_buildingLinkCard{
    padding:1.6rem;
    
     .cardTopImage-headerContainer{
      flex-direction: column;
     }
  }
}
</style>